import { useEffect, useState } from 'react';
import { authApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { C } from '../../theme/palette';

/** Les trois modules, dans les mots de l'écran de choix. */
const SECTEURS_SOCIETE = [
  ['batiment', '🏗️', 'Bâtiment'],
  ['magasin', '🛒', 'Magasin'],
  ['mixte', '🔗', 'Mix'],
];

/**
 * Le module d'une société, en clair. Une société tout juste ouverte n'en a
 * pas encore : elle le choisira avec sa formule.
 */
const libelleSecteur = (code) => {
  const trouve = SECTEURS_SOCIETE.find(([c]) => c === (code === 'chantier' ? 'batiment' : code));
  return trouve ? trouve.slice(1).join(' ') : '⏳ Module à choisir';
};

/**
 * Les sociétés du compte : la courante, celles où basculer, et en ouvrir une.
 *
 * <p>Le patron de deux sociétés devait tenir deux comptes et deux mots de
 * passe, et se déconnecter pour passer de l'une à l'autre. Il garde un seul
 * compte et bascule d'ici.
 *
 * <p>Basculer recharge la page entière. Chaque collection en mémoire — clients,
 * factures, stock — appartient à la société qu'on quitte, et le serveur ne
 * répond plus que pour la nouvelle : recharger est la seule façon sûre de n'en
 * garder aucune trace à l'écran.
 *
 * @param {object} props propriétés du composant
 * @param {() => void} [props.recharge] rechargement de la page, remplaçable en test
 * @returns {JSX.Element} la carte
 */
export function MesSocietesCard({ recharge = () => window.location.reload() }) {
  const { session, basculeSociete, creeSociete } = useSession();
  const [societes, setSocietes] = useState(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [ouvreForm, setOuvreForm] = useState(false);
  const [nom, setNom] = useState('');

  useEffect(() => {
    let annule = false;
    authApi
      .societes()
      .then((liste) => {
        if (!annule) setSocietes(liste || []);
      })
      .catch(() => {
        if (!annule) setErreur('Les sociétés du compte n’ont pas pu être lues.');
      });
    return () => {
      annule = true;
    };
  }, []);

  const bascule = async (id) => {
    setEnCours(true);
    setErreur('');
    try {
      await basculeSociete(id);
      recharge();
    } catch (e) {
      setErreur(e?.message || 'La bascule a échoué.');
      setEnCours(false);
    }
  };

  // La société naît sans module : la page rechargée la trouve ainsi et ouvre
  // l'assistant de départ, où se choisissent le module et la formule
  // (inscription d'abonnement), puis les coordonnées.
  const cree = async () => {
    if (!nom.trim()) {
      setErreur('Donnez un nom à la société.');
      return;
    }
    setEnCours(true);
    setErreur('');
    try {
      await creeSociete({ nom: nom.trim() });
      recharge();
    } catch (e) {
      setErreur(e?.message || 'La création a échoué.');
      setEnCours(false);
    }
  };

  const peutCreer = session?.role === 'patron' || session?.role === 'PATRON';

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="🏢"
        title="Mes sociétés"
        color={C.teal}
        sub="Un seul compte, plusieurs sociétés — chacune isolée des autres"
      />
      <div style={{ padding: 13 }}>
        {societes === null && !erreur && (
          <div style={{ fontSize: 8.5, color: C.muted }}>Lecture des sociétés…</div>
        )}
        {(societes || []).map((s) => (
          <div
            key={s.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '9px 11px',
              border: '1px solid ' + (s.courante ? C.teal : C.border),
              borderRadius: 10,
              marginBottom: 7,
              background: s.courante ? C.tealL : '#fff',
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: C.ink }}>{s.nom}</div>
              <div style={{ fontSize: 8.5, color: C.muted }}>
                {libelleSecteur(s.secteur)} · {String(s.role || '').toLowerCase()}
              </div>
            </div>
            {s.courante ? (
              <span style={{ fontSize: 10, fontWeight: 800, color: C.teal }}>
                ● Société ouverte
              </span>
            ) : (
              <Btn
                label="Basculer"
                sm
                color={C.navy}
                disabled={enCours}
                onClick={() => bascule(s.id)}
              />
            )}
          </div>
        ))}

        {peutCreer && !ouvreForm && (
          <Btn
            label="＋ Nouvelle société"
            sm
            color={C.teal}
            full
            disabled={enCours}
            onClick={() => setOuvreForm(true)}
          />
        )}

        {peutCreer && ouvreForm && (
          <div
            style={{
              border: '1px dashed ' + C.teal,
              borderRadius: 10,
              padding: 11,
              marginTop: 4,
            }}
          >
            <Inp
              label="Nom de la société"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="Ex : Mon Magasin Sàrl"
            />
            {/*
              Le module fixe le tarif et ce que la société peut ouvrir : il se
              choisit avec une formule, à l'ouverture de la société, comme à
              l'inscription. La carte le donnait d'un clic — le Mix compris —
              sans formule.
            */}
            <div
              style={{
                background: C.navyXL,
                borderRadius: 9,
                padding: '9px 11px',
                marginBottom: 10,
                fontSize: 10,
                lineHeight: 1.5,
              }}
            >
              La société s’ouvre sur le choix de son module (Bâtiment, Magasin ou Mix) et de sa
              formule, comme à l’inscription. Elle commence par un essai gratuit.
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ flex: 1 }}>
                <Btn
                  label="✓ Créer et choisir le module"
                  sm
                  color={C.green}
                  full
                  disabled={enCours}
                  onClick={cree}
                />
              </div>
              <Btn
                label="Annuler"
                sm
                color={C.muted}
                disabled={enCours}
                onClick={() => setOuvreForm(false)}
              />
            </div>
          </div>
        )}

        {erreur && (
          <div style={{ marginTop: 8, fontSize: 10, color: C.red, fontWeight: 700 }}>{erreur}</div>
        )}
      </div>
    </Card>
  );
}
